import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDemo } from "../context/DemoContext";
import { JoynMark } from "../components/Logo";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const colors = ["#F05A28", "#F7931D", "#D6DF23", "#4BB5E7", "#3AAA35"];

export default function About() {
  const { openDemo } = useDemo();
  const { t } = useTranslation();
  const beliefs = t("aboutPage.beliefs", { returnObjects: true });
  const advantages = t("aboutPage.advantages", { returnObjects: true });
  return (
    <div data-testid="about-page" className="pt-32 md:pt-40">
      <Seo {...PAGE_SEO["/about"]} path="/about" />
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-orange mb-4">{t("aboutPage.eyebrow")}</p>
        <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight leading-[0.98] max-w-4xl">
          {t("aboutPage.titleBefore")}<span className="joyn-gradient-text">{t("aboutPage.titleBrand")}</span>{t("aboutPage.titleAfter")}
        </h1>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed">{t("aboutPage.desc")}</p>
      </section>

      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="flex items-center gap-4 mb-10"><JoynMark size={40} /><h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">{t("aboutPage.beliefsTitle")}</h2></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {beliefs.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06, duration: 0.4 }} className="bg-card border border-border rounded-2xl p-6">
                <span className="block h-1.5 w-10 rounded-full mb-4" style={{ background: colors[i % colors.length] }} />
                <p className="leading-relaxed">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-cyan mb-4">{t("aboutPage.advEyebrow")}</p>
        <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mb-12 max-w-3xl">{t("aboutPage.advTitle")}</h2>
        <div className="space-y-4">
          {advantages.map((a, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
              className="group flex items-start gap-6 border border-border rounded-2xl p-7 hover:shadow-lg transition-shadow bg-card">
              <span className="font-display font-extrabold text-3xl tracking-tight shrink-0" style={{ color: colors[i] }}>{String(i + 1).padStart(2, "0")}</span>
              <div><h3 className="font-display font-bold text-xl tracking-tight mb-1">{a.t}</h3><p className="text-muted-foreground">{a.b}</p></div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="rounded-3xl bg-foreground text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="h-1.5 joyn-gradient-bar absolute top-0 inset-x-0" />
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-4">{t("aboutPage.ctaTitle")}</h2>
          <button data-testid="about-demo-btn" onClick={openDemo} className="mt-4 inline-flex items-center gap-2 bg-white text-foreground font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("nav.demo")} <ArrowRight size={18} /></button>
        </div>
      </section>
    </div>
  );
}
